import {useCallback, useEffect, useState} from "react";
import Api from "../../../core/services/api/api";
import useIsMounted from "../use-is-mounted";
import CacheService from "../../../core/services/cache/cache_service";
import LocalStorageService from "../../../core/services/cache/local_storage_service";

export const useAppVersion = () => {
    const [appVersion, setAppVersion] = useState(CacheService.appVersion ?? "1.0.0");
    const isMounted = useIsMounted();

    /**
     * Listens for changes to the app version in local storage and updates the state accordingly. This is useful for when the app version is updated in another tab or window.
     */
    useEffect(() => {
        const listener = (event) => {
            if (event.key === LocalStorageService.keys.appVersion) {
                setAppVersion(event.newValue ?? "1.0.0");
            }
        }
        window.addEventListener('storage', listener);
        return () => {
            window.removeEventListener('storage', listener);
        }
    })

    /**
     * Fetches the app version.
     */
    const getAppVersion = useCallback(async () => {
        const response = await Api.getVersionHistory();
        if (!isMounted())
            return;
        const versionsEntries = (Object.keys(response) ?? [])
            .map(e => [e, e.split('.').map(Number).filter(e => !Number.isNaN(e))])
            .filter(([, a]) => !!a.length)
            .sort(([, a], [, b]) => {
                // a and b are both lists of numbers, so go through each element one by one and compare
                // if the elements are equal, continue to the next element
                // if the elements are not equal, return the difference
                for (let i = 0; i < a.length; i++) {
                    if (a[i] !== b[i]) {
                        return Number(a[i]) - Number(b[i]);
                    }
                }
                return 0;
            })
        const appVersion = versionsEntries[versionsEntries.length - 1]?.[0];
        CacheService.setAppVersion(appVersion)
        setAppVersion(appVersion ?? '1.0.0');
    }, [isMounted])

    /**
     * As soon as the component mounts:
     * Fetches the app version.
     */
    useEffect(() => {
        getAppVersion().then();
    }, [getAppVersion]);

    return appVersion;
}